import type { BlurWorkerResponse } from "./backgroundBlurTypes";

const CDN_BASE =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18";
const BUNDLE_URL = `${CDN_BASE}/vision_bundle.mjs`;
const WASM_DIR_URL = `${CDN_BASE}/wasm`;
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/1/selfie_segmenter.tflite";
const INIT_TIMEOUT_MS = 15_000;

export type BackgroundBlurPipeline = {
  readonly outputStream: MediaStream;
  readonly destroy: () => void;
};

export const isBackgroundBlurSupported = (): boolean =>
  typeof MediaStreamTrackProcessor !== "undefined" &&
  typeof MediaStreamTrackGenerator !== "undefined";

export const createBackgroundBlurPipeline = async (
  inputStream: MediaStream,
): Promise<BackgroundBlurPipeline> => {
  // Dynamic import ensures the worker chunk is included in the bundle as a
  // separate entry point (matched by esbuild's `**/*.chunk.ts` glob).
  const { WorkerUrl } = await import("./backgroundBlur.worker.chunk");
  if (!WorkerUrl) {
    throw new Error("Background blur worker URL not available");
  }
  const worker = new Worker(WorkerUrl, { type: "module" });

  // Wait for worker to load MediaPipe model
  const initPromise = new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error("Background blur worker init timed out"));
    }, INIT_TIMEOUT_MS);

    const onMessage = (event: MessageEvent<BlurWorkerResponse>) => {
      const msg = event.data;
      if (msg.type === "ready") {
        clearTimeout(timeout);
        worker.removeEventListener("message", onMessage);
        resolve();
      } else if (msg.type === "error") {
        clearTimeout(timeout);
        worker.removeEventListener("message", onMessage);
        reject(new Error(msg.message));
      }
    };

    worker.addEventListener("message", onMessage);
  });

  worker.postMessage({
    type: "init",
    bundleUrl: BUNDLE_URL,
    wasmDirUrl: WASM_DIR_URL,
    modelUrl: MODEL_URL,
  });

  await initPromise;

  // Set up Insertable Streams pipeline
  const videoTrack = inputStream.getVideoTracks()[0];
  const processor = new MediaStreamTrackProcessor({ track: videoTrack });
  const generator = new MediaStreamTrackGenerator({ kind: "video" });

  const readable = processor.readable;
  const writable = generator.writable;

  worker.postMessage(
    { type: "start", readable, writable },
    // ReadableStream/WritableStream are transferable in Chrome but TS lib.dom.d.ts
    // does not include them in the Transferable union type
    [readable, writable] as unknown as Transferable[],
  );

  const outputStream = new MediaStream([generator]);

  // Copy audio tracks through unchanged
  for (const audioTrack of inputStream.getAudioTracks()) {
    outputStream.addTrack(audioTrack);
  }

  const destroy = () => {
    worker.postMessage({ type: "stop" });
    // Give worker a brief moment to clean up, then terminate
    setTimeout(() => {
      worker.terminate();
    }, 100);
    try {
      generator.stop();
    } catch {
      // Generator may already be stopped
    }
  };

  return { outputStream, destroy };
};
